// @flow
import React, { Component } from 'react';
import type { Node } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import * as R from 'ramda';

// Helpers
import classNames from 'classnames';
import bootstrap from 'vendor/bootstrap/_bootstrap_custom.scss';
import {
  getKey,
  getDisplay,
  createValue,
  getBodyTypesText
} from 'Common/formValues';
import { gtmEventPush } from 'utils/gtm/events';

// Components
import Icon from 'Common/Icon';
import CarType from 'Common/CarType';

/**
 * <CheckBoxGroup /> component.
 */
class CheckBoxGroup extends Component<*, *> {
  timeouts: any[];
  /**
   * @param {object} props - props passed
   */
  constructor(props: Object) {
    super(props);
    this.state = {
      filterValue: ''
    };
    this.timeouts = [];
  }

  componentWillUnmount() {
    this.timeouts.forEach(clearTimeout);
  }

  /**
   * Implements propTypes().
   *
   * @return {Object} - propType object.
   * @prop {object} styles - Input styles.
   * @prop {object} bootstrap - bootstrap styles
   * @prop {string} name - input name.
   * @prop {string} label - label
   * @prop {string} selectedValue - value
   * @prop {array} options - options
   * @prop {bool} disableCounts - disableCounts
   * @prop {func} onSelect - onSelect
   * @prop {func} setAsSelected - setAsSelected
   * @prop {bool} isSelected - isSelected
   * @prop {bool} isSwoosh - isSwoosh
   * @prop {func} resetField - resetField
   * @prop {bool} isDisabled - isDisabled
   * @prop {bool} isCarTypes - isCarTypes
   */
  static propTypes = {
    styles: PropTypes.object.isRequired,
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    selectedValue: PropTypes.string,
    options: PropTypes.array,
    disableCounts: PropTypes.bool,
    isSelected: PropTypes.bool,
    onSelect: PropTypes.func,
    onClose: PropTypes.func,
    setAsSelected: PropTypes.func,
    isSwoosh: PropTypes.bool,
    resetField: PropTypes.func,
    isDisabled: PropTypes.bool,
    disableFiltering: PropTypes.bool,
    isCarTypes: PropTypes.bool
  };

  /**
   * updateFilterValue.
   * @param {string} value - value.
   *
   * @return {void}
   */
  updateFilterValue: (e: SyntheticInputEvent<*>) => void = e => {
    this.setState({
      filterValue: e.target.value
    });
  };

  handleFilterInputBlur: () => void = () => {
    const { label } = this.props;
    gtmEventPush({
      method: 'filterForm',
      args: {
        itemLabel: `${label}: value manually entered`
      }
    });
  };

  handleSelected: (e: SyntheticEvent<*>) => void = e => {
    const { label, isSelected, onSelect, onClose } = this.props;
    if (!isSelected) {
      // Menu is being opened
      onSelect(e);
      gtmEventPush({
        method: 'filterForm',
        args: {
          itemLabel: `${label}: menu opened`
        }
      });
    } else {
      // Menu is being closed
      onClose();
    }
  };

  handleSelectValue: (e: SyntheticEvent<*>) => void = () => {
    const { label, name, resetField, setAsSelected } = this.props;
    gtmEventPush({
      method: 'filterForm',
      args: {
        itemLabel: `${label}: value selected`
      }
    });
    if (name === 'make' && resetField) {
      resetField('model');
      resetField('trim');
    }
    if (name === 'model' && resetField) {
      resetField('trim');
    }
    if (setAsSelected) {
      setTimeout(() => setAsSelected(''), 250);
    }
  };

  handleFilterClear: () => void = () => {
    const { onClose, resetField } = this.props;
    resetField();
    onClose();
  };

  /**
   * Renders <Input /> component.
   *
   * @return {JSX} - renders an Input.
   */
  render(): Node {
    const {
      styles,
      name,
      label,
      selectedValue,
      options,
      disableCounts,
      isSelected,
      isSwoosh,
      isDisabled,
      disableFiltering,
      isCarTypes,
      isMobile
    } = this.props;

    const filteredOptions = options.filter(option => {
      if (typeof option.key === 'string') {
        return R.contains(
          this.state.filterValue.toLowerCase(),
          option.key.toLowerCase()
        );
      }
      return true;
    });

    /**
     * Renders <isChecked.
     * @param {object} item - item.
     * @param {boolean} selectedValue - selectedValue.
     *
     * @return {boolean} - returns isChecked
     */
    const isChecked = (item, selectedValue) => {
      if (item.key) {
        return item.key === getKey(selectedValue);
      }

      return false;
    };

    const isOptions = () => options.length !== 0;

    return (
      <div
        className={classNames(
          styles.checkBoxGroup,
          isCarTypes ? styles.carTypeCheckBoxGroup : null
        )}
        data-selectedcheckboxgroup={isSelected}
        data-name={name}
      >
        <button
          type="button"
          onClick={this.handleSelected}
          disabled={isDisabled || !isOptions()}
        >
          {label}{' '}
          {selectedValue && (
            <span> ({getBodyTypesText(getDisplay(selectedValue))})</span>
          )}
          <Icon
            iconName={isSelected ? 'chevron-up-small' : 'chevron-down-small'}
            size={'default'}
            className={classNames(
              styles.icon,
              isSwoosh && !isMobile ? styles.iconChevronSwoosh : null
            )}
          />
        </button>
        {isSelected && (
          <div
            className={classNames(
              styles.checkboxes,
              isSwoosh ? styles.swoosh : null
            )}
          >
            {isSwoosh && <span className={styles.swooshLabel}>{label}</span>}
            {!disableFiltering && (
              <input
                className={styles.filter}
                type="text"
                placeholder="Type to filter"
                value={this.state.filterValue}
                onChange={this.updateFilterValue}
                onBlur={this.handleFilterInputBlur}
              />
            )}
            <div
              className={classNames(
                styles.checkboxesInner,
                isCarTypes ? styles.checkboxesInnerCarTypes : null
              )}
            >
              {!isCarTypes && (
                <div>
                  {filteredOptions.map((item, index) => {
                    return (
                      <div key={index}>
                        <label
                          className={classNames(
                            styles.container,
                            item.key && item.key === getKey(selectedValue)
                              ? styles.isSelected
                              : null
                          )}
                          key={index}
                        >
                          {item.display || item.key.toString()}
                          {!disableCounts && (
                            <span className={styles.count}>
                              ({item.doc_count.toLocaleString('en')})
                            </span>
                          )}
                          <Field
                            name={name}
                            className={styles.checkBox}
                            value={createValue(item)}
                            placeholder={label}
                            onClick={this.handleSelectValue}
                            component="input"
                            type={'radio'}
                            checked={isChecked(item, selectedValue)}
                          />
                          <span className={styles.checkmark} />
                        </label>
                      </div>
                    );
                  })}
                </div>
              )}

              {isCarTypes &&
                filteredOptions.map((item, index) => {
                  return (
                    <div key={index}>
                      <CarType
                        key={index}
                        firstOfType={index === 0}
                        name={'category'}
                        size={'medium-xlarge'}
                        label={item.key}
                        results={item.doc_count || 0}
                        selectedValue={selectedValue}
                        value={createValue(item)}
                        isResults={true}
                      />
                    </div>
                  );
                })}
            </div>
            <button
              className={classNames(
                bootstrap.btn,
                bootstrap.btnLink,
                bootstrap.btnLg,
                styles.clearOptionsButton
              )}
              type="button"
              onClick={this.handleFilterClear}
            >
              Clear
            </button>
          </div>
        )}
      </div>
    );
  }
}

export default CheckBoxGroup;



// WEBPACK FOOTER //
// ./src/Common/CheckBoxGroup/index.js